import { ReactNode, useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, FormControlLabel, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SectionHeading } from "./ChecklistRow";
import AttachmentTiles from "./AttachmentTiles";
import CustomerContactField, { ContactFields } from "./CustomerContactField";
import DigitalDetailsFields from "./DigitalDetailsFields";
import RefundFields from "./RefundFields";
import TrackingNumberField from "./TrackingNumberField";
import SectionRow, { RowStatus } from "./SectionRow";
import { packageFileOf } from "./packageFiles";
import { EvidenceAttachments } from "../../hooks/useEvidenceAttachments";
import {
  ALWAYS_OFFERED_SLOTS,
  SECTION_ROWS,
  conclusionAsk,
  contactUnanswered,
  itemsForRow,
  needsInput,
  openItems,
  rowIsOn,
  sectionRow,
  unlistedFiles,
  type SectionRowSpec,
} from "./sectionRows";
import {
  DigitalDetails,
  DocumentSection,
  EXCLUDABLE_SECTIONS,
  EvidenceAttachmentRef,
  EvidenceAttentionCode,
  EvidenceAttentionItem,
  EvidenceDraftSummary,
  EvidenceOutline,
  ExcludableSection,
  FactId,
  ForceableSection,
  ManualRefund,
  OutlineRefund,
  OutlineSection,
  PackageProvider,
  SECTION_IDS,
} from "../../types/evidence";

const REFUND_STORE_NO_SUMMARY = "Refunded per the store record.";
const REFUND_MANUAL = "Prints from your screenshot and figures.";
const REFUND_NONE_ON = "No refund on the order. Attach the refund screenshot and fill in the amount and date, or the section stays out.";
const REFUND_NONE_OFF = "No refund on the order. Tick to add one you issued elsewhere.";
const REFUND_LEFT_OUT = "Left out.";
const UNLISTED = "Not printed: this document has no section for these files.";
const NO_CONTACT = "Support desk checked: no contact";
const NO_CONTACT_PROOF = "Support desk search";
const CONTACT_ASK = "Say whether the customer contacted support.";
const PRINTED_AT_REQUEST = "Printed at your request.";
const IP_LEFT_OUT = " The IP Location section is left out.";
const FOCUS_FLASH_MS = 1200;
const TRACKING_GAPS: EvidenceAttentionCode[] = ["tracking_unavailable", "tracking_pending"];

export type SectionRowKey = DocumentSection | "conclusion";

function toggled<T extends string>(list: readonly T[] | undefined, key: T, checked: boolean): T[] {
  const without = (list ?? []).filter((entry) => entry !== key);
  return checked ? without : [...without, key];
}

// A row the agent can tick in is never locked, whichever list it sits on.
const isLocked = (row: OutlineSection) => !(EXCLUDABLE_SECTIONS as readonly string[]).includes(row.key) && !row.forceable;
const NOT_PRINTED = "Not printed for this order.";
const hasFile = (files: EvidenceAttachmentRef[], slot: string) => files.some((f) => f.slot === slot);

// Only an open ask flags the row; every line itself is plain text.
function rowStatus(
  row: OutlineSection, spec: SectionRowSpec, on: boolean, forced: boolean, open: EvidenceAttentionItem[], refund: OutlineRefund, files: EvidenceAttachmentRef[], noContactAttested: boolean, contactOpen: boolean,
): RowStatus | null {
  // Asked on the row it is about, whether or not that row prints yet.
  if (row.key === "customerCommunication" && contactOpen) return { text: CONTACT_ASK, ask: "recommended" };
  if (row.key === "proofOfRefund") {
    if (!on) return refund.source === "none" ? { text: REFUND_NONE_OFF, ask: null } : { text: REFUND_LEFT_OUT, ask: null };
    if (refund.source === "store") return { text: refund.summary ?? REFUND_STORE_NO_SUMMARY, ask: null };
    if (refund.source === "manual" && hasFile(files, "proof_of_refund")) return { text: REFUND_MANUAL, ask: null };
    return { text: REFUND_NONE_ON, ask: "recommended" };
  }
  // The row's own body already says where its content comes from.
  if (!spec.source) return null;
  // A lookup that failed has nothing to print even when ticked, so only a mismatch reads as printed.
  const mismatch = open.find((item) => item.code === "ip_location_mismatch");
  if (on && forced && row.forceable && (row.key === "orderHistory" || row.key === "checkoutProcess" || mismatch)) {
    return { text: mismatch ? `Printed at your request: ${mismatch.message.replace(IP_LEFT_OUT, "")}` : PRINTED_AT_REQUEST, ask: null };
  }
  if (row.key === "ipLocation" && !row.prints) {
    return { text: open.find((item) => spec.notes?.includes(item.code))?.message ?? spec.source, ask: null };
  }
  if (!on) return { text: spec.source, ask: null };
  // A note under a printing row only informs.
  if (open[0]) return { text: open[0].message, ask: open[0].attachableSlot ? open[0].severity : null };
  // A screenshot here is the support desk search itself, so it proves the same "no contact".
  if (row.key === "customerCommunication" && noContactAttested) {
    return { text: NO_CONTACT, ask: null };
  }
  return { text: spec.source, ask: null };
}

// Every section of the document in print order. A tick is what prints; unticked rows stay listed so
// a section left out can be put back.
export default function SectionsCard({
  draft, outline, items, attachments, disabled, error, productDescription, conclusion, provider, renderedIds, onJump, onHover, focusSection, hoverSection,
  onSaveExcludes, onSaveRefund, onSaveContact, onSaveDigitalDetails, onSaveTrackingNumber, onContactUnsavedChange, onRefundUnsavedChange, onDigitalDetailsUnsavedChange,
}: {
  draft: EvidenceDraftSummary;
  outline: EvidenceOutline | null;
  items: EvidenceAttentionItem[];
  attachments: EvidenceAttachments;
  disabled: boolean;
  error: boolean;
  productDescription: ReactNode;
  /** The conclusion's status and actions, shown as the document's last row. */
  conclusion: ReactNode;
  provider: PackageProvider | null;
  /** The data-section ids the preview holds; a row whose section is among them jumps to it. */
  renderedIds: string[];
  onJump: (sectionIds: string[]) => void;
  /** The rendered ids of the row under the pointer, or none once it leaves. */
  onHover: (sectionIds: string[]) => void;
  /** A row to scroll to and flash; a new `at` repeats it for the same row. */
  focusSection: { key: SectionRowKey; at: number } | null;
  /** The row whose section is under the pointer in the preview. */
  hoverSection?: SectionRowKey | null;
  onSaveExcludes: (body: { excludedSections?: ExcludableSection[]; excludedFacts?: FactId[]; includedSections?: ForceableSection[] }) => Promise<boolean>;
  onSaveRefund: (value: ManualRefund | null) => Promise<boolean>;
  onSaveContact: (fields: ContactFields) => Promise<boolean>;
  onSaveDigitalDetails: (value: DigitalDetails) => Promise<boolean>;
  onSaveTrackingNumber: (value: string | null) => Promise<boolean>;
  onContactUnsavedChange: (unsaved: boolean) => void;
  onRefundUnsavedChange: (unsaved: boolean) => void;
  onDigitalDetailsUnsavedChange: (unsaved: boolean) => void;
}) {
  const savedSections = draft.input_overrides?.excludedSections ?? [];
  const savedIncluded = draft.input_overrides?.includedSections ?? [];
  const savedFacts = draft.input_overrides?.excludedFacts ?? [];
  // Local copy so a second click before the first save lands builds on the first one.
  const [excludedSections, setExcludedSections] = useState<ExcludableSection[]>(savedSections);
  const [includedSections, setIncludedSections] = useState<ForceableSection[]>(savedIncluded);
  const [excludedFacts, setExcludedFacts] = useState<FactId[]>(savedFacts);
  const savedSectionsKey = JSON.stringify(savedSections);
  const savedIncludedKey = JSON.stringify(savedIncluded);
  const savedFactsKey = JSON.stringify(savedFacts);
  useEffect(() => { setExcludedSections(JSON.parse(savedSectionsKey)); }, [savedSectionsKey]);
  useEffect(() => { setIncludedSections(JSON.parse(savedIncludedKey)); }, [savedIncludedKey]);
  useEffect(() => { setExcludedFacts(JSON.parse(savedFactsKey)); }, [savedFactsKey]);
  // Rows the run left off that the agent ticked: shown on here, saved nowhere until they print.
  const [revealed, setRevealed] = useState<Set<DocumentSection>>(() => new Set());
  useEffect(() => { setRevealed(new Set()); }, [draft.draft_id]);
  const rowRefs = useRef(new Map<SectionRowKey, HTMLDivElement>());
  const rowRef = (key: SectionRowKey) => (el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(key, el); else rowRefs.current.delete(key);
  };
  const [flashed, setFlashed] = useState<SectionRowKey | null>(null);

  const saveSections = (next: ExcludableSection[]) => {
    setExcludedSections(next);
    void onSaveExcludes({ excludedSections: next }).then((ok) => { if (!ok) setExcludedSections(savedSections); });
  };
  const saveIncluded = (next: ForceableSection[]) => {
    setIncludedSections(next);
    void onSaveExcludes({ includedSections: next }).then((ok) => { if (!ok) setIncludedSections(savedIncluded); });
  };
  const saveFacts = (next: FactId[]) => {
    setExcludedFacts(next);
    void onSaveExcludes({ excludedFacts: next }).then((ok) => { if (!ok) setExcludedFacts(savedFacts); });
  };

  const files = attachments.attachments;
  const stillOpen = openItems(items, files, draft.no_contact_attested);
  const contactOpen = contactUnanswered(draft);
  // Any contact answer prints Customer Communication by itself, screenshot or not.
  const answerPrints = draft.no_contact_attested || Boolean(draft.input_overrides?.customerContact);
  const rows = outline && !error ? outline.sections : [];
  // A section that cannot be left out and has nothing to print is shown as not printed, unless the
  // agent can still add what makes it print: an ask on one of its slots, a file, or an offered tile.
  const lockedOff = (row: OutlineSection) => isLocked(row) && !row.prints
    && !itemsForRow(row.key, stillOpen).some((item) => item.attachableSlot)
    && !SECTION_ROWS[row.key].slots.some((slot) => hasFile(files, slot) || ALWAYS_OFFERED_SLOTS.includes(slot))
    && row.generatedSlots.length === 0;
  const trackingNumber = draft.input_overrides?.trackingNumber ?? null;
  const withNumber = (shown: Set<DocumentSection>) => (trackingNumber ? new Set(shown).add("fulfillment") : shown);
  const numberOnly = (row: OutlineSection) => row.key === "fulfillment" && !row.prints && !itemsForRow(row.key, items).some((item) => item.attachableSlot);
  const isOn = (row: OutlineSection) => isLocked(row)
    ? !lockedOff(row)
    : rowIsOn(row, excludedSections, includedSections, withNumber(revealed), itemsForRow(row.key, stillOpen), numberOnly(row) ? [] : files, answerPrints);
  const isForced = (row: OutlineSection) => row.forceable && includedSections.includes(row.key as ForceableSection);
  // A tick-in left on a row that can no longer be forced is dropped with the next save of the list.
  const forceableOnly = (next: ForceableSection[]) => next.filter((key) => rows.some((row) => row.key === key && row.forceable));
  const onByItself = (row: OutlineSection) => rowIsOn(row, [], [], withNumber(new Set()), itemsForRow(row.key, stillOpen), numberOnly(row) ? [] : files, answerPrints);
  const toggleRow = (row: OutlineSection, checked: boolean) => {
    const key = row.key as ExcludableSection;
    // A rule left this row out; the agent's tick overrides it and is saved, so the section prints.
    if (!row.prints && !onByItself(row) && row.forceable && !excludedSections.includes(key)) {
      saveIncluded(forceableOnly(toggled(includedSections, row.key as ForceableSection, !checked)));
      return;
    }
    setRevealed((prev) => {
      const next = new Set(prev);
      if (checked) next.add(row.key); else next.delete(row.key);
      return next;
    });
    // A row only this page revealed has nothing to leave out: unticking it only hides it again.
    if (checked ? excludedSections.includes(key) : onByItself(row)) saveSections(toggled(excludedSections, key, checked));
  };
  // The attachment list decides what is still open; until it loads nothing can be judged.
  const judged = !attachments.unknown;
  const conclusionOpen = conclusionAsk(draft) !== null;
  const needing = outline && judged
    ? rows.filter((row) => needsInput(row, isOn(row), itemsForRow(row.key, stillOpen), outline.refund, files, contactOpen)).length + Number(conclusionOpen)
    : 0;
  const leftOut = savedSections.filter((key) => rows.some((row) => row.key === key)).length + savedFacts.length;
  const unlisted = judged ? unlistedFiles(rows, files) : [];
  // Opened by what needs input when the outline first arrives; after that only the agent opens or
  // shuts it, so a refetch or a filled slot never snaps it shut, and a click made before the
  // outline landed is kept.
  const [expanded, setExpanded] = useState<{ draftId: number; open: boolean } | null>(null);
  const shown = expanded?.draftId === draft.draft_id ? expanded.open : null;
  if (shown === null && outline && judged) setExpanded({ draftId: draft.draft_id, open: needing > 0 });

  // A click in the preview opens the list, brings its row into view and flashes it.
  // Keyed by the click, not the object, so a re-render that rebuilds the prop never flashes again.
  const draftId = draft.draft_id;
  const focusKey = focusSection?.key;
  const focusAt = focusSection?.at;
  useEffect(() => {
    if (!focusKey) return;
    setExpanded({ draftId, open: true });
    setFlashed(focusKey);
    rowRefs.current.get(focusKey)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    const timer = setTimeout(() => setFlashed(null), FOCUS_FLASH_MS);
    return () => { clearTimeout(timer); setFlashed(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new focus only, never a new draft
  }, [focusKey, focusAt]);

  const headerCounts = [
    leftOut > 0 && { text: `· ${leftOut} left out`, color: "text.secondary" },
    needing > 0 && { text: `· ${needing} needs input`, color: "warning.dark" },
  ].filter((part) => part !== false);

  // A row's slots sit side by side, each named under its tiles.
  const tiles = (slots: EvidenceAttachmentRef["slot"][], generatedSlots: readonly EvidenceAttachmentRef["slot"][] = []) => slots.length > 0 && (
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} sx={{ mt: 1 }}>
      {slots.map((slot) => (
        <AttachmentTiles
          key={slot}
          draftId={draft.draft_id}
          slot={slot}
          attachments={attachments}
          generated={generatedSlots.includes(slot)}
          disabled={disabled}
          caption={slot === "customer_communication" && draft.no_contact_attested ? NO_CONTACT_PROOF : true}
        />
      ))}
    </Stack>
  );

  const fileTag = (section: DocumentSection) => provider ? packageFileOf(provider, section) : undefined;
  // A row whose section rendered jumps to it on a click and outlines it on hover.
  const linked = (key: SectionRowKey, ids: readonly string[]) => {
    const rendered = ids.filter((id) => renderedIds.includes(id));
    return {
      ref: rowRef(key),
      selected: flashed === key,
      hovered: hoverSection === key,
      ...(rendered.length > 0 && {
        onClick: () => onJump(rendered),
        onHover: (hovering: boolean) => onHover(hovering ? rendered : []),
      }),
    };
  };

  // Needs no outline, so it stays reachable when the section list fails to load.
  const conclusionRow = (
    <SectionRow heading="Conclusion" locked checked disabled={disabled} onToggle={() => {}} status={null} tag={fileTag("factsOfTheCase")} {...linked("conclusion", ["conclusion"])}>
      {conclusion}
    </SectionRow>
  );

  const controls = (row: OutlineSection, on: boolean, refund: OutlineRefund): ReactNode => {
    if (row.key === "proofOfRefund") {
      // Unticking keeps the typed figures on screen, closed, rather than clearing them.
      const fields = refund.source !== "store" && (on || refund.source === "manual");
      return (
        <>
          {on && tiles(["proof_of_refund"])}
          {fields && (
            <RefundFields manualRefund={draft.input_overrides?.manualRefund ?? null} disabled={disabled || !on} onSave={onSaveRefund} onUnsavedChange={onRefundUnsavedChange} />
          )}
        </>
      );
    }
    // The agent's own answer keeps the row on: an "Other" that drops the attestation holding it on
    // must not unmount the field while its sentence is still being typed.
    const saveContact = (fields: ContactFields) => {
      setRevealed((prev) => new Set(prev).add(row.key));
      return onSaveContact(fields);
    };
    const contact = row.key === "customerCommunication" && outline && (
      <CustomerContactField draft={draft} template={outline.template} disabled={disabled} onSave={saveContact} onUnsavedChange={onContactUnsavedChange} />
    );
    const tracking = row.key === "fulfillment" && (trackingNumber || items.some((item) => TRACKING_GAPS.includes(item.code))) && (
      <TrackingNumberField trackingNumber={trackingNumber} disabled={disabled} onSave={onSaveTrackingNumber} />
    );
    if (!on) return null;
    const rowItems = itemsForRow(row.key, items);
    // An attested support desk may still attach its empty search as proof.
    // A ticked-in Checkout Process has no asks of its own, so both its tiles are offered.
    const offered = (slot: EvidenceAttachmentRef["slot"]) => ALWAYS_OFFERED_SLOTS.includes(slot)
      || (slot === "customer_communication" && draft.no_contact_attested)
      || (row.key === "checkoutProcess" && row.forceable);
    // The product photo is edited beside its description, never as a tile.
    const slots = SECTION_ROWS[row.key].slots.filter((slot) => slot !== "product_photo" && (
      hasFile(files, slot) || rowItems.some((item) => item.attachableSlot === slot) || row.generatedSlots.includes(slot) || offered(slot)));
    return (
      <>
        {contact}
        {tracking}
        {row.key === "productDescription" && productDescription}
        {row.key === "factsOfTheCase" && outline && (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5 }}>
            {outline.facts.map((fact) => (
              <FormControlLabel
                key={fact.id}
                label={<Typography variant="body2">{fact.text}</Typography>}
                sx={{ alignItems: "flex-start", mt: 0.5, mx: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    sx={{ pt: 0, pl: 0 }}
                    checked={!excludedFacts.includes(fact.id)}
                    disabled={disabled || fact.id === "order" || fact.id === "disputedCharge"}
                    onChange={(event) => saveFacts(toggled(excludedFacts, fact.id, event.target.checked))}
                  />
                )}
              />
            ))}
          </Box>
        )}
        {tiles(slots, row.generatedSlots)}
        {row.key === "digitalUsage" && (
          <DigitalDetailsFields digitalDetails={draft.input_overrides?.digitalDetails} disabled={disabled} onSave={onSaveDigitalDetails} onUnsavedChange={onDigitalDetailsUnsavedChange} />
        )}
        {/* A screenshot already keeps this row on, so the generated table needs its own tick. */}
        {row.forceable && onByItself(row) && (
          <FormControlLabel
            label={<Typography variant="body2">{`Print the ${SECTION_ROWS[row.key].heading} table too`}</Typography>}
            sx={{ mt: 0.5, mx: 0 }}
            control={(
              <Checkbox
                size="small"
                sx={{ pt: 0, pl: 0 }}
                checked={isForced(row)}
                disabled={disabled}
                onChange={(event) => saveIncluded(forceableOnly(toggled(includedSections, row.key as ForceableSection, !event.target.checked)))}
              />
            )}
          />
        )}
      </>
    );
  };

  return (
    <>
      {/* Outside the card: a list that never loaded leaves it shut, and nothing in it can be attached. */}
      {attachments.error && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={attachments.refetch}>Retry</Button>}
        >
          Could not load attached screenshots — retry.
        </Alert>
      )}
      <Accordion
        expanded={shown ?? false}
        onChange={(_, next) => setExpanded({ draftId: draft.draft_id, open: next })}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: 1, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
          <SectionHeading>
            Sections
            {headerCounts.map(({ text, color }) => (
              <Box key={text} component="span" sx={{ color, textTransform: "none", letterSpacing: 0 }}>{text}</Box>
            ))}
          </SectionHeading>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0 }}>
          {error ? (
            <>
              <Typography variant="body2" color="error">
                The section list could not be loaded. Reload the page to try again.
              </Typography>
              {conclusionRow}
            </>
          ) : outline ? (
            <>
              <Box>
                {rows.map((row) => {
                  const on = isOn(row);
                  const heading = SECTION_ROWS[row.key].heading;
                  const storeRefund = row.key === "proofOfRefund" && outline.refund.source === "store";
                  return (
                    <SectionRow
                      key={row.key}
                      heading={heading}
                      locked={isLocked(row)}
                      notPrinted={lockedOff(row)}
                      checked={on}
                      disabled={disabled}
                      onToggle={(checked) => toggleRow(row, checked)}
                      tag={fileTag(row.key)}
                      {...linked(row.key, SECTION_IDS[row.key])}
                      chip={storeRefund ? <Chip size="small" label="from the store" /> : undefined}
                      status={lockedOff(row) ? { text: NOT_PRINTED, ask: null } : rowStatus(row, sectionRow(row.key, outline.template), on, isForced(row), itemsForRow(row.key, stillOpen), outline.refund, files, draft.no_contact_attested, contactOpen)}
                    >
                      {controls(row, on, outline.refund)}
                    </SectionRow>
                  );
                })}
                {unlisted.length > 0 && (
                  <SectionRow heading="Other screenshots" locked={false} checked={false} disabled onToggle={() => {}} status={{ text: UNLISTED, ask: null }}>
                    {tiles([...new Set(unlisted.map((f) => f.slot))])}
                  </SectionRow>
                )}
                {conclusionRow}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Generate the evidence first — the sections are read from the document.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}
